import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';
import { useEntityTypes } from '@/features/settings/hooks/useEntityTypes';
import { useJobEntityTypes } from '@/features/jobs/hooks/useJobEntityTypes';
import { useFormDefinitions } from './hooks/useFormBuilder';

type EntityScope = 'contact' | 'job';

interface EntityTypeFormMapping {
  id: string;
  tenant_id: string;
  entity_scope: string;
  entity_type_id: string;
  form_definition_id: string;
  is_default: boolean;
  created_at: string;
}

function useMappings(entityScope: EntityScope) {
  return useQuery({
    queryKey: ['form-builder', 'mappings', entityScope],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<EntityTypeFormMapping[]>>(
        `/form-builder/mappings/entity-types?entity_scope=${entityScope}`,
      );
      return res.data.data ?? [];
    },
  });
}

/**
 * FormMappingsPage — assign form definitions to entity types across scopes (Contact / Job).
 * Enforces one form per entity type within each scope.
 */
export function FormMappingsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedScope, setSelectedScope] = useState<EntityScope>('contact');
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [selectedForm, setSelectedForm] = useState('');

  const { data: mappings, isLoading: mappingsLoading } = useMappings(selectedScope);
  const { data: contactEntityTypes } = useEntityTypes();
  const { data: jobEntityTypes } = useJobEntityTypes();
  const { data: forms } = useFormDefinitions(selectedScope);

  const entityTypes = selectedScope === 'contact' ? contactEntityTypes : jobEntityTypes;

  const createMutation = useMutation({
    mutationFn: async (data: {
      entity_scope: EntityScope;
      entity_type_id: string;
      form_definition_id: string;
    }) => {
      const res = await apiClient.post<ApiResponse<EntityTypeFormMapping>>(
        '/form-builder/mappings/entity-types',
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-builder', 'mappings'] });
      toast({ variant: 'success', title: 'Form assigned to entity type' });
      setSelectedEntityType('');
      setSelectedForm('');
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast({
        variant: 'error',
        title: err.response?.data?.message || 'Failed to assign form',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/form-builder/mappings/entity-types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-builder', 'mappings'] });
      toast({ variant: 'success', title: 'Mapping removed' });
    },
  });

  const handleAssign = () => {
    if (!selectedEntityType || !selectedForm) return;
    createMutation.mutate({
      entity_scope: selectedScope,
      entity_type_id: selectedEntityType,
      form_definition_id: selectedForm,
    });
  };

  const handleScopeChange = (scope: EntityScope) => {
    setSelectedScope(scope);
    setSelectedEntityType('');
    setSelectedForm('');
  };

  const getEntityTypeName = (id: string) => entityTypes?.find((et) => et.id === id)?.name ?? id;

  const getFormName = (id: string) => forms?.find((f) => f.id === id)?.name ?? id;

  // Entity types that don't already have a mapping
  const mappedEntityTypeIds = new Set(mappings?.map((m) => m.entity_type_id) ?? []);
  const unmappedEntityTypes = (entityTypes ?? []).filter(
    (et) => et.isActive && !mappedEntityTypeIds.has(et.id),
  );

  if (mappingsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/settings/form-builder')}
        className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400"
      >
        &larr; Back to Form Builder
      </button>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Form Mappings</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Assign which form definition each entity type uses. Each entity type can have exactly one
          form assigned.
        </p>
      </div>

      {/* Entity Scope Selector */}
      <Card>
        <CardHeader title="Entity Scope" />
        <CardContent>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="entity_scope"
                value="contact"
                checked={selectedScope === 'contact'}
                onChange={() => handleScopeChange('contact')}
                className="text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Contact</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="entity_scope"
                value="job"
                checked={selectedScope === 'job'}
                onChange={() => handleScopeChange('job')}
                className="text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Job</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Assign Form */}
      <Card>
        <CardHeader
          title={`Assign Form to ${selectedScope === 'contact' ? 'Entity Type' : 'Job Type'}`}
        />
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Select
                label={selectedScope === 'contact' ? 'Entity Type' : 'Job Type'}
                value={selectedEntityType}
                onChange={(e) => setSelectedEntityType(e.target.value)}
                options={unmappedEntityTypes.map((et) => ({ value: et.id, label: et.name }))}
                placeholder={
                  selectedScope === 'contact' ? 'Select entity type...' : 'Select job type...'
                }
              />
            </div>
            <div className="flex-1">
              <Select
                label="Form Definition"
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
                options={(forms ?? [])
                  .filter((f) => f.is_active)
                  .map((f) => ({ value: f.id, label: f.name }))}
                placeholder="Select form..."
              />
            </div>
            <Button
              onClick={handleAssign}
              disabled={!selectedEntityType || !selectedForm}
              loading={createMutation.isPending}
            >
              Assign
            </Button>
          </div>
          {unmappedEntityTypes.length === 0 && (
            <p className="mt-3 text-xs text-gray-500">
              All {selectedScope === 'contact' ? 'entity types' : 'job types'} already have a form
              assigned.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Current Mappings */}
      <Card>
        <CardHeader title="Current Mappings" />
        <CardContent>
          {!mappings || mappings.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">
              No mappings configured for{' '}
              {selectedScope === 'contact' ? 'entity types' : 'job types'} yet.
            </p>
          ) : (
            <div className="divide-y divide-border dark:divide-border-dark">
              {mappings.map((mapping) => (
                <div key={mapping.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={mapping.entity_scope === 'contact' ? 'default' : 'warning'}
                      size="sm"
                    >
                      {mapping.entity_scope === 'contact' ? 'Contact' : 'Job'}
                    </Badge>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {getEntityTypeName(mapping.entity_type_id)}
                    </span>
                    <span className="text-gray-400">&rarr;</span>
                    <Badge variant="info" size="sm">
                      {getFormName(mapping.form_definition_id)}
                    </Badge>
                    {mapping.is_default && (
                      <Badge variant="success" size="sm">
                        Default
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMutation.mutate(mapping.id)}
                    loading={deleteMutation.isPending}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
